// src/pages/AppVersionsPage.js
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import styles from './AppVersionsPage.module.css';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AppVersionCard from '../components/settings/AppVersionCard';
import { fetchAppVersions, PLATFORMS, readApiError } from '../services/appVersionApi';

// Sunucu her iki platformu da döndürür; yine de eksik gelen platform için boş
// kart çizmek (ve "yapılandırılmamış" göstermek) doğru davranış.
const normalize = (rows) =>
    PLATFORMS.map(({ value }) => {
        const found = rows.find((row) => row?.platform === value);
        return found || {
            platform: value,
            minSupportedVersion: '',
            latestVersion: '',
            storeUrl: '',
            enabled: false,
            updatedAt: null,
            updatedBy: null,
        };
    });

const AppVersionsPage = () => {
    const [configs, setConfigs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const load = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setIsLoading(true);
        try {
            const rows = await fetchAppVersions();
            setConfigs(normalize(rows));
            setLoadError(null);
        } catch (error) {
            const { message } = readApiError(error, 'Sürüm ayarları yüklenemedi.');
            setLoadError(message);
            if (silent) toast.error(message);
        } finally {
            if (!silent) setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Kaydettikten sonra listeyi yeniden çekiyoruz: updatedAt / updatedBy yalnızca
    // sunucuda üretiliyor, tahmin edilemez.
    const handleSaved = useCallback(() => {
        load({ silent: true });
    }, [load]);

    if (isLoading) {
        return (
            <div className={styles.loadingContainer}>
                <LoadingSpinner />
                <p>Uygulama sürümleri yükleniyor...</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerText}>
                    <h1 className={styles.title}>Uygulama Sürümleri</h1>
                    <p className={styles.description}>
                        Mobil uygulamanın zorunlu ve önerilen güncelleme eşiklerini platform bazında yönetin.
                    </p>
                </div>
            </div>

            <div className={styles.infoPanel}>
                <h2 className={styles.infoTitle}>Nasıl çalışır</h2>
                <ul className={styles.infoList}>
                    <li>
                        <strong>Zorunlu minimum sürüm</strong> — bu sürümün <strong>altındaki</strong> kullanıcılar
                        uygulamayı kullanamaz; kapatılamayan bir güncelleme ekranı görürler.
                    </li>
                    <li>
                        <strong>Güncel sürüm</strong> — eşiğin üstünde ama bunun altında olan kullanıcılara
                        güncelleme yalnızca <strong>önerilir</strong>; kapatıp devam edebilirler.
                    </li>
                    <li>Eşiğe <strong>eşit</strong> sürüm ne zorlanır ne önerilir.</li>
                    <li>
                        Kademeli mağaza dağıtımı (staged rollout) bitmeden <strong>güncel sürümü</strong> yükseltmeyin —
                        kullanıcı mağazada henüz göremeyeceği bir sürüme yönlendirilmesin.
                    </li>
                </ul>
            </div>

            {loadError && (
                <div className={styles.errorPanel}>
                    <span>{loadError}</span>
                    <button type="button" className={styles.retryButton} onClick={() => load()}>
                        Tekrar dene
                    </button>
                </div>
            )}

            <div className={styles.cardGrid}>
                {configs.map((config) => (
                    <AppVersionCard key={config.platform} config={config} onSaved={handleSaved} />
                ))}
            </div>
        </div>
    );
};

export default AppVersionsPage;
